import type { Control } from "./Control.js";
import type { Point } from "../../common/geometry.js";

export interface KeyboardBridgeHandlers {
  keydown(e: KeyboardEvent): void;
  keyup(e: KeyboardEvent): void;
  /** Committed text — a typed character, a paste, or an IME commit. */
  textInput(text: string): void;
  /**
   * The field's text changed: replace [start, end) of the focused control's
   * text with `text`. Only sent while the control mirrors its text into the
   * field (`setMirroredText`), because only then do the offsets mean anything.
   */
  replaceText(start: number, end: number, text: string): void;
  /** IME composition in progress; the preview should be shown inline. */
  compositionUpdate(text: string): void;
  compositionEnd(): void;
  /** Return the text to place on the clipboard, or null to let the event pass. */
  copyRequested(): string | null;
  /** Same as copy, but the source should delete its selection. */
  cutRequested(): string | null;
  paste(text: string): void;
  /** The whole app lost keyboard focus (user tabbed to browser chrome). */
  blur(): void;
  /** What the focused control needs of the hidden field, if anything. */
  textEntry(): TextEntryState;
}

/**
 * Decide what the hidden field should be doing for `control`.
 *
 * Pure and exported, because this one rule is what decides whether a phone
 * raises its on-screen keyboard, and it is worth being able to test without
 * a DOM.
 */
export function textEntryFor(control: Control | null, field?: TextEntryField | null): TextEntryState {
  if (!control?.acceptsTextInput) return { active: false, readOnly: false };
  const state: TextEntryState = {
    active: true,
    readOnly: (control as { readOnly?: boolean }).readOnly === true,
  };
  if (field) state.field = field;
  return state;
}

/** One edit to a string: `before.slice(start, end)` became `text`. */
export interface TextEdit {
  start: number;
  end: number;
  text: string;
}

/**
 * Work out what one edit did to a field, from its text before and after.
 *
 * The common suffix is never allowed to reach left of the caret, so an
 * ambiguous edit ("aa" to "aaa") is placed where the caret says it was made.
 * Returns null when nothing changed. Pure and exported so it tests without a
 * DOM.
 */
export function diffEdit(before: string, after: string, caretAfter = after.length): TextEdit | null {
  if (before === after) return null;
  const caret = Math.max(0, Math.min(caretAfter, after.length));
  let suffix = 0;
  const maxSuffix = Math.min(before.length, after.length - caret);
  while (suffix < maxSuffix && before[before.length - 1 - suffix] === after[after.length - 1 - suffix]) suffix++;
  let prefix = 0;
  const maxPrefix = Math.min(before.length, after.length) - suffix;
  while (prefix < maxPrefix && before[prefix] === after[prefix]) prefix++;
  return { start: prefix, end: before.length - suffix, text: after.slice(prefix, after.length - suffix) };
}

/** An element the bridge can route through instead of its own hidden field. */
export type TextEntryField = HTMLTextAreaElement | HTMLInputElement;

export interface TextEntryState {
  /** The focused control consumes typed characters or supports copying. */
  active: boolean;
  /**
   * ...but cannot be typed into. Mirrored onto the hidden field, because a
   * read-only field is exactly how the platform is told to leave the
   * on-screen keyboard down while keeping selection and copy working.
   */
  readOnly: boolean;
  /**
   * Route through this field rather than the shared hidden one.
   *
   * Set for a control an `AutofillForm` shadows: a password manager offers a
   * saved credential to the field the caret is in, so while such a control has
   * focus the field the caret is in has to be that form's own input.
   */
  field?: TextEntryField;
}

/**
 * Routes keyboard input through one of two hosts: a hidden <textarea>
 * overlaid on the canvas, or the canvas itself.
 *
 * The textarea is the Monaco/Docs approach, and it is the only way to get IME
 * composition, dead keys and native clipboard events without permission
 * prompts. It is moved to the caret position so IME candidate windows appear
 * in the right place.
 *
 * It is *not* focused all the time, though, which it used to be. Focusing a
 * textarea is what raises the on-screen keyboard on a phone, so an
 * always-focused one meant every tap anywhere — a button, a window title bar,
 * bare desktop — brought the keyboard up over the app. Focus only moves there
 * when the focused control actually consumes typed characters; the rest of
 * the time the canvas holds focus, which delivers keydown and keyup just as
 * well and raises nothing.
 */
export class KeyboardBridge {
  readonly element: HTMLTextAreaElement;
  #handlers: KeyboardBridgeHandlers;
  #host: HTMLElement;
  #canvas: HTMLCanvasElement;
  #composing = false;
  #attached = false;
  #accessible = false;
  #keyboardHost: HTMLElement;
  /**
   * What each field held when the bridge last knew it, so an input event can
   * be turned into the edit that produced it. Present only for a field whose
   * text a control is mirroring; without one, offsets would mean nothing.
   */
  #known = new WeakMap<TextEntryField, string>();
  /** A mirror requested mid-composition, applied when the composition ends. */
  #pendingMirror: { text: string; start: number; end: number } | null = null;

  /**
   * Told whenever keystrokes start arriving somewhere else.
   *
   * The element holding DOM focus is not one element: it is the canvas for a
   * button, the hidden field for a text box, and an autofill input for a text
   * box a password manager is shadowing. Anything decorating the focused
   * element - `aria-activedescendant`, above all - has to follow it, or it
   * goes silent at exactly the moment a text field takes focus.
   */
  onHostChanged: ((host: HTMLElement, previous: HTMLElement | null) => void) | null = null;
  /** Adopted fields, plus `element` - every DOM node keystrokes may arrive on. */
  #fields = new Set<TextEntryField>();

  constructor(canvas: HTMLCanvasElement, handlers: KeyboardBridgeHandlers) {
    this.#handlers = handlers;
    this.#canvas = canvas;

    const host = canvas.parentElement ?? document.body;
    this.#host = host;
    this.#keyboardHost = canvas;
    if (getComputedStyle(host).position === "static" && host !== document.body) {
      host.style.position = "relative";
    }

    const ta = document.createElement("textarea");
    ta.className = "webwin-input";
    ta.autocapitalize = "off";
    ta.autocomplete = "off";
    ta.spellcheck = false;
    ta.tabIndex = 0;
    ta.setAttribute("aria-hidden", "true");
    ta.setAttribute("wrap", "off");
    Object.assign(ta.style, {
      position: "absolute",
      left: "0px",
      top: "0px",
      width: "1px",
      height: "1px",
      padding: "0",
      margin: "0",
      border: "0",
      outline: "none",
      resize: "none",
      overflow: "hidden",
      opacity: "0",
      // Kept in the layout (not display:none) so the IME can position itself,
      // but never a pointer target — the canvas handles all pointing.
      pointerEvents: "none",
      whiteSpace: "pre",
      zIndex: "1",
    } satisfies Partial<CSSStyleDeclaration>);
    host.appendChild(ta);
    this.element = ta;

    // The canvas is the other keyboard host, so it has to be focusable. A page
    // that already set tabindex keeps its value.
    if (!canvas.hasAttribute("tabindex")) canvas.tabIndex = 0;

    this.#attach();
  }

  #attach(): void {
    if (this.#attached) return;
    this.#attached = true;
    this.adopt(this.element);
    // The canvas carries the same key handlers, because it is the host
    // whenever no text control is focused.
    this.#canvas.addEventListener("keydown", this.#onKeyDown);
    this.#canvas.addEventListener("keyup", this.#onKeyUp);
    this.#canvas.addEventListener("mousedown", this.#onCanvasMouseDown);
    this.#canvas.addEventListener("focus", this.#onCanvasFocus);
  }

  /**
   * Route text entry through `field` as well, for whenever `textEntry` names
   * it. The field is the caller's to place, size and remove; all the bridge
   * does is listen on it exactly as it listens on its own.
   */
  adopt(field: TextEntryField): void {
    if (this.#fields.has(field)) return;
    this.#fields.add(field);
    for (const [type, listener] of this.#listeners()) field.addEventListener(type, listener);
  }

  /** Stop routing through `field`. Hands text entry back to the hidden one. */
  release(field: TextEntryField): void {
    if (field === this.element || !this.#fields.delete(field)) return;
    for (const [type, listener] of this.#listeners()) field.removeEventListener(type, listener);
  }

  /**
   * Every listener a field carries, in one list.
   *
   * A list rather than ten calls twice over, because a field released still
   * holding one of them is a field the browser goes on delivering keystrokes
   * through after its control is gone - and the two lists drift silently.
   */
  #listeners(): [string, EventListener][] {
    return [
      ["keydown", this.#onKeyDown as EventListener],
      ["keyup", this.#onKeyUp as EventListener],
      ["input", this.#onInput as EventListener],
      ["compositionstart", this.#onCompositionStart as EventListener],
      ["compositionupdate", this.#onCompositionUpdate as EventListener],
      ["compositionend", this.#onCompositionEnd as EventListener],
      ["copy", this.#onCopy as EventListener],
      ["cut", this.#onCut as EventListener],
      ["paste", this.#onPaste as EventListener],
      ["blur", this.#onBlur as EventListener],
    ];
  }

  /**
   * The browser's default action for mousedown on the canvas would move focus
   * onto it, which is right when nothing is being typed into and wrong when
   * something is. Suppress it and place focus deliberately instead.
   */
  #onCanvasMouseDown = (e: MouseEvent): void => {
    e.preventDefault();
    this.sync();
  };

  /**
   * Tabbing in from browser chrome lands on the canvas, which is a perfectly
   * good host — but hand over to the hidden field if what got focused wants
   * text. Deferred, because browsers ignore a focus change made synchronously
   * from inside a focus event.
   */
  #onCanvasFocus = (): void => {
    setTimeout(() => this.sync(), 0);
  };

  get composing(): boolean {
    return this.#composing;
  }

  /**
   * Put DOM focus on whichever host suits the control that has Webwin focus.
   *
   * Call this after anything that can change the focused control, and only
   * from inside a user gesture when it might turn text entry on: browsers
   * raise the on-screen keyboard for a programmatic focus only when one is in
   * progress.
   */
  sync(): void {
    this.setTextEntry(this.#handlers.textEntry());
  }

  /** Focus a field or the canvas, per `state`. */
  setTextEntry(state: TextEntryState): void {
    const field = state.active ? this.#fieldOf(state) : this.element;
    field.readOnly = state.readOnly;
    const wanted = state.active ? field : this.#canvas;
    // Announced before the early return: the host is where keystrokes are
    // *meant* to go, and on the first call DOM focus is already there.
    this.#setHost(wanted);
    if (document.activeElement === wanted) return;
    // Blurring the field is what dismisses a phone's keyboard, so it has to
    // happen even when the canvas cannot take focus for some reason.
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== wanted && this.#fields.has(active as TextEntryField)) {
      active.blur();
    }
    wanted.focus({ preventScroll: true });
  }

  /**
   * The field text entry is going through: the hidden one unless the focused
   * control has been given one of its own.
   *
   * Asked of the handlers every time rather than remembered, because a control
   * raises its own `focus` event *before* the focus manager reports the change
   * - so a remembered answer is one control stale for exactly as long as it
   * takes the new field to be told what is in it, and the text lands in the
   * field belonging to the control just left. With one shared field that was
   * invisible; with a field per control it emptied the box behind you as you
   * tabbed out of it.
   */
  get field(): TextEntryField {
    return this.#fieldOf(this.#handlers.textEntry());
  }

  #fieldOf(state: TextEntryState): TextEntryField {
    return state.field && this.#fields.has(state.field) ? state.field : this.element;
  }

  blur(): void {
    this.field.blur();
  }

  /** True while text entry is live, meaning one of the fields has focus. */
  get hasFocus(): boolean {
    const active = document.activeElement;
    return active !== null && this.#fields.has(active as TextEntryField);
  }

  /** True while either host has focus, so keystrokes reach the app. */
  get hasKeyboard(): boolean {
    return this.hasFocus || document.activeElement === this.#canvas;
  }

  /**
   * Move the hidden field to the caret so IME candidates appear there.
   *
   * An adopted field is already where its control is - that is what makes a
   * password manager's drop-down land under the box it fills - so moving it
   * would be moving it off the thing it stands for.
   */
  setCaretPosition(p: Point | null): void {
    if (this.field !== this.element) return;
    const s = this.element.style;
    if (!p) {
      s.left = "0px";
      s.top = "0px";
      return;
    }
    s.left = `${Math.round(p.x)}px`;
    s.top = `${Math.round(p.y)}px`;
  }

  /** The element keystrokes are currently going to: a field, or the canvas. */
  get keyboardHost(): HTMLElement {
    return this.#keyboardHost;
  }

  #setHost(next: HTMLElement): void {
    if (this.#keyboardHost === next) return;
    const previous = this.#keyboardHost;
    this.#keyboardHost = next;
    this.onHostChanged?.(next, previous);
  }

  /**
   * Let a screen reader see the field the caret is actually in.
   *
   * The hidden textarea is marked `aria-hidden` by default, and that flatly
   * cancels what `setMirroredText` is for: the browser is already holding the
   * focused control's text and selection in a real editable element, with
   * caret announcements, character echo, word echo and selection echo that
   * nothing drawn on a canvas can imitate. Un-hiding it is the single largest
   * thing this whole layer does, and it is this method.
   *
   * Off by default all the same, because the element is one pixel wide at
   * zero opacity: worth exposing to somebody who asked for an accessible
   * desktop, not worth exposing to everybody else by surprise.
   */
  setAccessible(on: boolean): void {
    if (this.#accessible === on) return;
    this.#accessible = on;
    if (on) this.element.removeAttribute("aria-hidden");
    else this.element.setAttribute("aria-hidden", "true");
  }

  /**
   * Mirror the focused control's selection so the browser's native copy/cut
   * carries the right payload and - once `setAccessible` is on - so a screen
   * reader reads the real caret rather than nothing at all.
   */
  setMirroredText(text: string, selectionStart: number, selectionEnd: number): void {
    // Rewriting the field under a running composition breaks the IME; it is
    // caught up when the composition ends.
    if (this.#composing) {
      this.#pendingMirror = { text, start: selectionStart, end: selectionEnd };
      return;
    }
    this.#pendingMirror = null;
    const field = this.field;
    if (field.value !== text) field.value = text;
    this.#known.set(field, text);
    try {
      field.setSelectionRange(selectionStart, selectionEnd);
    } catch {
      // setSelectionRange throws on detached elements; harmless.
    }
  }

  #onKeyDown = (e: KeyboardEvent): void => {
    // During IME composition the raw keydowns (including Enter and arrows)
    // belong to the candidate window, not to us.
    if (this.#composing || e.isComposing || e.keyCode === 229) return;
    this.#handlers.keydown(e);
  };

  #onKeyUp = (e: KeyboardEvent): void => {
    if (this.#composing || e.isComposing) return;
    this.#handlers.keyup(e);
  };

  /**
   * Every change to the field: typed, composed, deleted or autocorrected.
   *
   * While a control mirrors its text into the field, the field is the truth:
   * the edit is recovered by diffing its text before and after, and applied
   * as a range replacement. That is the only reading that survives an Android
   * keyboard, which composes every word, re-opens a composition over a word
   * already typed (so a composition's text is not new text), and deletes with
   * keyCode 229 and an input event carrying no data. Treating composed text as
   * an insertion typed "admin@admin@jetsonsoft.admin@..." on a phone, and
   * backspace went missing until the keyboard rewrote a whole word.
   */
  #onInput = (e: Event): void => {
    const input = e as InputEvent;
    const field = e.target as TextEntryField;
    const before = this.#known.get(field) ?? null;
    // Paste is handled by its own event so it can be routed as one edit.
    if (input.inputType === "insertFromPaste") return;
    if (before === null) {
      // No mirror: a control that takes characters but keeps no text the
      // field can stand for. Committed characters are all it can use.
      if (!this.#composing && input.data) this.#handlers.textInput(input.data);
      this.#resetValue();
      return;
    }
    // The browser's own undo would rewind to a value the control never had.
    if (input.inputType === "historyUndo" || input.inputType === "historyRedo") {
      field.value = before;
      return;
    }
    const after = field.value;
    this.#known.set(field, after);
    const edit = diffEdit(before, after, field.selectionEnd ?? after.length);
    if (edit) this.#handlers.replaceText(edit.start, edit.end, edit.text);
  };

  #onCompositionStart = (): void => {
    this.#composing = true;
  };

  #onCompositionUpdate = (e: CompositionEvent): void => {
    // A mirrored field shows composed text as real text, edit by edit.
    if (this.#known.has(e.target as TextEntryField)) return;
    this.#handlers.compositionUpdate(e.data ?? "");
  };

  #onCompositionEnd = (e: CompositionEvent): void => {
    this.#composing = false;
    this.#handlers.compositionEnd();
    if (this.#known.has(e.target as TextEntryField)) {
      // Catch the field up with whatever the control made of the edits.
      const pending = this.#pendingMirror;
      if (pending) this.setMirroredText(pending.text, pending.start, pending.end);
      return;
    }
    if (e.data) this.#handlers.textInput(e.data);
    this.#resetValue();
  };

  #onCopy = (e: ClipboardEvent): void => {
    const text = this.#handlers.copyRequested();
    if (text === null) return;
    e.clipboardData?.setData("text/plain", text);
    e.preventDefault();
  };

  #onCut = (e: ClipboardEvent): void => {
    const text = this.#handlers.cutRequested();
    if (text === null) return;
    e.clipboardData?.setData("text/plain", text);
    e.preventDefault();
  };

  #onPaste = (e: ClipboardEvent): void => {
    const text = e.clipboardData?.getData("text/plain");
    e.preventDefault();
    if (text) this.#handlers.paste(text);
    this.#resetValue();
  };

  #onBlur = (): void => {
    this.#composing = false;
    this.#pendingMirror = null;
    this.#handlers.blur();
  };

  /** Keep the mirror short so the browser never scrolls it into view. */
  #resetValue(): void {
    const field = this.field;
    if (field.value.length > 4096) field.value = "";
  }

  dispose(): void {
    const ta = this.element;
    for (const field of [...this.#fields]) {
      if (field !== ta) this.release(field);
    }
    for (const [type, listener] of this.#listeners()) ta.removeEventListener(type, listener);
    this.#fields.clear();
    this.#canvas.removeEventListener("keydown", this.#onKeyDown);
    this.#canvas.removeEventListener("keyup", this.#onKeyUp);
    this.#canvas.removeEventListener("mousedown", this.#onCanvasMouseDown);
    this.#canvas.removeEventListener("focus", this.#onCanvasFocus);
    ta.remove();
    this.#attached = false;
    void this.#host;
  }
}
